export const EMPTY_REFERENCE_LINK = { url: "", description: "" };

export const parseReferenceLinks = (value) => {
  if (!value) {
    return [{ ...EMPTY_REFERENCE_LINK }];
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return [{ ...EMPTY_REFERENCE_LINK }];
    }

    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && parsed.length) {
          return parsed.map((entry) => ({
            url: entry?.url || "",
            description: entry?.description || "",
          }));
        }
      } catch {
        // fall through to legacy comma-separated URLs
      }
    }

    return trimmed.split(",").map((url) => ({
      url: url.trim(),
      description: "",
    }));
  }

  if (Array.isArray(value)) {
    if (!value.length) {
      return [{ ...EMPTY_REFERENCE_LINK }];
    }

    if (typeof value[0] === "string") {
      return value.filter(Boolean).map((url) => ({
        url: String(url).trim(),
        description: "",
      }));
    }

    return value.map((entry) => ({
      url: entry?.url || "",
      description: entry?.description || "",
    }));
  }

  return [{ ...EMPTY_REFERENCE_LINK }];
};

export const formatReferenceLinks = (otherLinks) => {
  const entries = (otherLinks || [])
    .filter((entry) => entry?.url?.trim())
    .map((entry) => ({
      url: entry.url.trim(),
      description: entry.description?.trim() || "",
    }));

  return entries.length ? entries : undefined;
};

export const validateLinkDescriptionContent = (trimmed) => {
  if (!/[A-Za-z0-9]/.test(trimmed)) {
    return "Special characters only are not allowed";
  }

  if (/^[0-9\s]+$/.test(trimmed)) {
    return "Numbers only are not allowed";
  }

  if (!/[A-Za-z]/.test(trimmed)) {
    return "Link description must contain at least one alphabet";
  }

  return null;
};

export const getLinkUrlRules = (fieldName, listName = "otherLinks") => [
  ({ getFieldValue }) => ({
    validator(_, value) {
      const trimmed = value?.trim();
      const description = getFieldValue([listName, fieldName, "description"])?.trim();

      if (!trimmed && !description) {
        return Promise.resolve();
      }

      if (!trimmed) {
        return Promise.reject(new Error("Please enter a valid URL"));
      }

      try {
        const parsed = new URL(trimmed);
        if (!["http:", "https:"].includes(parsed.protocol)) {
          return Promise.reject(new Error("Please enter a valid URL"));
        }
      } catch {
        return Promise.reject(new Error("Please enter a valid URL"));
      }

      return Promise.resolve();
    },
  }),
];

export const getLinkDescriptionRules = (fieldName, listName = "otherLinks") => [
  ({ getFieldValue }) => ({
    validator(_, value) {
      const url = getFieldValue([listName, fieldName, "url"])?.trim();
      const trimmed = value?.trim();

      if (!url) {
        return Promise.resolve();
      }

      if (!trimmed) {
        return Promise.reject(
          new Error(
            "Link description is required when a reference link is provided",
          ),
        );
      }

      const contentError = validateLinkDescriptionContent(trimmed);
      if (contentError) {
        return Promise.reject(new Error(contentError));
      }

      if (trimmed.length < 10) {
        return Promise.reject(
          new Error("Link description must be at least 10 characters"),
        );
      }

      if (trimmed.length > 500) {
        return Promise.reject(
          new Error("Link description cannot exceed 500 characters"),
        );
      }

      return Promise.resolve();
    },
  }),
];
